import { useEffect, useState } from 'react';
import { SITE } from '../data/site';

/**
 * Live Google review data as returned by GET /api/reviews
 * (see functions/api/reviews.ts NormalizedReviewsResponse).
 */
export interface GoogleReview {
  author: string;
  authorUri: string | null;
  authorPhoto: string | null;
  rating: number;
  text: string;
  relativeTime: string | null;
  publishTime: string | null;
  googleMapsUri: string | null;
}

export interface GoogleReviewsData {
  rating: number | null;
  reviewCount: number;
  googleMapsUri: string | null;
  fetchedAt: string;
  reviews: GoogleReview[];
  /** Curated testimonial rotation (recent 5-star reviews with text) for the
      floating widget; may be absent on responses cached before it shipped. */
  curated?: GoogleReview[];
  curatedHarvestedAt?: string | null;
}

export type GoogleReviewsState =
  | { status: 'loading'; data: null }
  | { status: 'ready'; data: GoogleReviewsData }
  | { status: 'error'; data: null };

// Module-level cache so every component that calls useGoogleReviews() on a
// given page load shares a single /api/reviews request instead of each
// mounting its own fetch (Home's slider, the floating widget, and the
// Reviews page can all be mounted at once).
let cachedData: GoogleReviewsData | null = null;
let inFlight: Promise<GoogleReviewsData | null> | null = null;

async function fetchReviews(): Promise<GoogleReviewsData | null> {
  if (cachedData) return cachedData;
  if (!inFlight) {
    inFlight = fetch('/api/reviews')
      .then(async (res) => {
        if (!res.ok) return null;
        const json = (await res.json()) as GoogleReviewsData;
        cachedData = json;
        return json;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/**
 * Loads live Google reviews once per page session.
 *
 * IMPORTANT: `data` is `null` while loading and on any failure (missing
 * secret, Google outage, rate limit). Consumers must never fabricate a
 * rating/count/review while `status !== 'ready'` — render nothing, or a
 * neutral "See our reviews on Google" link, instead.
 */
export function useGoogleReviews(): GoogleReviewsState {
  const [state, setState] = useState<GoogleReviewsState>(() =>
    cachedData ? { status: 'ready', data: cachedData } : { status: 'loading', data: null }
  );

  useEffect(() => {
    if (cachedData) {
      setState({ status: 'ready', data: cachedData });
      return;
    }

    let cancelled = false;
    fetchReviews().then((data) => {
      if (cancelled) return;
      setState(data ? { status: 'ready', data } : { status: 'error', data: null });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Deep link to the practice's Google review list. */
export function googleReviewsUrl(placeId: string = SITE.googlePlaceId): string {
  return `https://search.google.com/local/reviews?placeid=${placeId}`;
}

/** Deep link to Google's "write a review" flow for the practice. */
export function googleWriteReviewUrl(placeId: string = SITE.googlePlaceId): string {
  return `https://search.google.com/local/writereview?placeid=${placeId}`;
}

/** Truncates review text for compact layouts, breaking on a word boundary. */
export function truncateReviewText(text: string, maxLength = 320): { text: string; truncated: boolean } {
  if (text.length <= maxLength) return { text, truncated: false };
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return { text: `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLength)}...`, truncated: true };
}
